import { useCallback, useMemo, useState } from 'react'
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts'
import ChartContainer from './ChartContainer'
import { RechartsChartTooltipProvider, StableRechartsChartTooltip } from './ChartTooltip'
import { formatCompactNumber } from './formatters'
import './chart.css'

const formatCurrencyAxis = (value) => `$${formatCompactNumber(value)}`
const formatRoasAxis = (value) => `${Number(value).toFixed(1)}x`

const getAxisTickFormatter = (metric) => {
  if (metric?.key === 'roas' || metric?.valueType === 'roas') return formatRoasAxis
  return formatCurrencyAxis
}

const numericAxisLabel = (value, side = 'left') =>
  value
    ? {
        value,
        angle: -90,
        position: side === 'right' ? 'right' : 'left',
        offset: 12,
        fill: 'var(--chart-muted)',
        fontSize: 10,
        style: { textAnchor: 'middle' },
      }
    : undefined

const PaidMediaPerformanceChart = ({
  data,
  series,
  height = 260,
  xKey = 'date',
  xTickFormatter,
  labelFormatter,
  loading,
  emptyMessage,
}) => {
  const metricMap = useMemo(
    () => Object.fromEntries((series || []).map((item) => [item.key, item])),
    [series]
  )
  const seriesKeys = useMemo(() => (series || []).map((item) => item.key), [series])
  const defaultLeft = seriesKeys[0] || 'spend'
  const defaultRight = seriesKeys[1] || 'revenue'

  const [axisSelection, setAxisSelection] = useState({
    left: defaultLeft,
    right: defaultRight,
  })

  const resolvedLeft = metricMap[axisSelection.left] ? axisSelection.left : defaultLeft
  const resolvedRight = metricMap[axisSelection.right] ? axisSelection.right : defaultRight
  const leftMetric = metricMap[resolvedLeft]
  const rightMetric = metricMap[resolvedRight]
  const usesDualAxis = Boolean(leftMetric && rightMetric && resolvedLeft !== resolvedRight)

  const visibleSeries = useMemo(
    () => [leftMetric, rightMetric].filter(Boolean),
    [leftMetric, rightMetric]
  )

  const onLegendClick = useCallback((key) => {
    setAxisSelection(({ left, right }) => {
      if (key === left) return { left: right, right: left }
      if (key === right) return { left: right, right: left }
      return { left, right: key }
    })
  }, [])

  const empty = !data || data.length === 0

  return (
    <div className="paidMediaPerformanceChart">
      <ChartContainer height={height} loading={loading} empty={empty} emptyMessage={emptyMessage} forceRender>
        <RechartsChartTooltipProvider
          series={visibleSeries}
          options={{
            labelFormatter,
          }}
        >
          <LineChart
            data={data}
            margin={{
              top: 10,
              right: usesDualAxis ? 52 : 20,
              left: 52,
              bottom: 10,
            }}
          >
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey={xKey}
              stroke="var(--chart-muted)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'var(--chart-border)' }}
              minTickGap={24}
              interval="preserveStartEnd"
              tickFormatter={xTickFormatter}
            />
            <YAxis
              yAxisId="left"
              stroke="var(--chart-muted)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: 'var(--chart-border)' }}
              width={48}
              tickFormatter={leftMetric ? getAxisTickFormatter(leftMetric) : undefined}
              label={numericAxisLabel(leftMetric?.label, 'left')}
            />
            {usesDualAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="var(--chart-muted)"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: 'var(--chart-border)' }}
                width={48}
                tickFormatter={rightMetric ? getAxisTickFormatter(rightMetric) : undefined}
                label={numericAxisLabel(rightMetric?.label, 'right')}
              />
            )}
            <Tooltip content={StableRechartsChartTooltip} />
            {leftMetric && (
              <Line
                type="monotone"
                yAxisId="left"
                dataKey={resolvedLeft}
                stroke={`var(${leftMetric.colorVar})`}
                strokeWidth={2}
                dot={false}
                name={leftMetric.label}
              />
            )}
            {rightMetric && resolvedRight !== resolvedLeft && (
              <Line
                type="monotone"
                yAxisId="right"
                dataKey={resolvedRight}
                stroke={`var(${rightMetric.colorVar})`}
                strokeWidth={2}
                dot={false}
                name={rightMetric.label}
              />
            )}
          </LineChart>
        </RechartsChartTooltipProvider>
      </ChartContainer>

      <div
        className="campaignPerformanceLegend paidMediaPerformanceChartLegend"
        role="group"
        aria-label="Paid media chart metrics"
      >
        {(series || []).map((item) => {
          const isActive = item.key === resolvedLeft || item.key === resolvedRight

          return (
            <button
              key={item.key}
              type="button"
              className="campaignPerformanceLegendItem paidMediaPerformanceChartLegendItem"
              onClick={() => onLegendClick(item.key)}
              aria-pressed={isActive}
            >
              <span
                className="campaignPerformanceLegendSwatch"
                style={{ backgroundColor: `var(${item.colorVar})`, opacity: isActive ? 1 : 0.35 }}
              />
              <span style={{ opacity: isActive ? 1 : 0.45 }}>{item.label}</span>
            </button>
          )
        })}
      </div>
      <p className="paidMediaPerformanceChartLegendHint">
        Click legend items to compare two metrics at a time.
      </p>
    </div>
  )
}

export default PaidMediaPerformanceChart
